/**
 * KWC hero slider.
 *
 * Vanilla, no dependencies. The markup works without this file: slide 1 is
 * visible, its link is live, and the SHOP NOW notch already points at it.
 * This only adds movement, so a JS failure degrades to a static banner.
 *
 * Autoplay is paused whenever the hero is not actually being looked at:
 * pointer over it, focus inside it, tab hidden, or hero scrolled out of view.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var SWIPE_THRESHOLD = 40;

  function initHero(root) {
    var track = root.querySelector('[data-kwc-hero-track]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-kwc-slide]'));
    if (!track || slides.length === 0) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-kwc-dot]'));
    var cta = root.querySelector('[data-kwc-hero-cta]');
    var ctaLabel = root.querySelector('[data-kwc-hero-cta-label]');
    var prevBtn = root.querySelector('[data-kwc-hero-prev]');
    var nextBtn = root.querySelector('[data-kwc-hero-next]');
    var isFade = root.getAttribute('data-transition') === 'fade';

    var index = 0;
    var timer = null;
    var paused = { pointer: false, focus: false, hidden: false, offscreen: false };

    var autoplayOn = root.getAttribute('data-autoplay') === 'true' && slides.length > 1;
    var delay = parseInt(root.getAttribute('data-autoplay-ms'), 10) || 5000;

    function render() {
      if (isFade) {
        slides.forEach(function (slide, i) {
          slide.classList.toggle('is-active', i === index);
        });
      } else {
        track.style.transform = 'translateX(' + -100 * index + '%)';
      }

      slides.forEach(function (slide, i) {
        var active = i === index;
        // inert keeps offscreen slides out of the tab order and the a11y tree.
        if (active) {
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('inert', '');
        }
        var link = slide.querySelector('.kwc-hero__slide-link');
        if (link) link.setAttribute('tabindex', active ? '0' : '-1');
      });

      dots.forEach(function (dot, i) {
        var active = i === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      // SHOP NOW always targets the slide the shopper is actually looking at.
      var current = slides[index];
      if (cta && current) {
        var href = current.getAttribute('data-link');
        if (href) cta.setAttribute('href', href);
        var label = current.getAttribute('data-label');
        if (ctaLabel && label) ctaLabel.textContent = label;
      }
    }

    function goTo(next) {
      var count = slides.length;
      index = ((next % count) + count) % count;
      render();
    }

    function shouldRun() {
      return (
        autoplayOn &&
        !REDUCED.matches &&
        !paused.pointer &&
        !paused.focus &&
        !paused.hidden &&
        !paused.offscreen
      );
    }

    function restart() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      if (shouldRun()) {
        timer = setInterval(function () {
          goTo(index + 1);
        }, delay);
      }
    }

    function setPaused(key, value) {
      paused[key] = value;
      restart();
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goTo(index - 1);
        restart();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goTo(index + 1);
        restart();
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        goTo(parseInt(dot.getAttribute('data-index'), 10) || 0);
        restart();
      });
    });

    root.addEventListener('pointerenter', function () { setPaused('pointer', true); });
    root.addEventListener('pointerleave', function () { setPaused('pointer', false); });
    root.addEventListener('focusin', function () { setPaused('focus', true); });
    root.addEventListener('focusout', function () {
      if (!root.contains(document.activeElement)) setPaused('focus', false);
    });

    document.addEventListener('visibilitychange', function () {
      setPaused('hidden', document.hidden);
    });

    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        goTo(index - 1);
        restart();
      } else if (event.key === 'ArrowRight') {
        goTo(index + 1);
        restart();
      }
    });

    // Touch swipe. Horizontal intent only, so vertical page scrolling is never
    // hijacked.
    var startX = 0;
    var startY = 0;
    var tracking = false;

    root.addEventListener(
      'pointerdown',
      function (event) {
        if (event.pointerType === 'mouse') return;
        startX = event.clientX;
        startY = event.clientY;
        tracking = true;
      },
      { passive: true }
    );

    root.addEventListener(
      'pointerup',
      function (event) {
        if (!tracking) return;
        tracking = false;
        var dx = event.clientX - startX;
        var dy = event.clientY - startY;
        if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
        goTo(dx < 0 ? index + 1 : index - 1);
        restart();
      },
      { passive: true }
    );

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            setPaused('offscreen', !entry.isIntersecting);
          });
        },
        { threshold: 0.2 }
      ).observe(root);
    }

    if (REDUCED.addEventListener) {
      REDUCED.addEventListener('change', restart);
    }

    render();
    restart();
  }

  function init() {
    document.querySelectorAll('[data-kwc-hero]').forEach(initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme Editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-kwc-hero]');
    if (root) initHero(root);
  });
})();
